import { byId } from "./domUtils";
import { paintNowPlayingPanel } from "./nowPlayingPlaylistController";
import { applyTopicCover } from "./topicCoverLoader";
import { coverUrlForTopic, categoriesFromClassification } from "../../domain/topicCover";
import { getTopic } from "./topicStore";
import type { MediaSessionArtworkImage } from "./mediaSessionController";
import type { Root } from "./prototypeTypes";
import { getRuntime } from './runtimeAccessor';
import type { MountedPlayer } from './mediaPlayerTypes';
import { topicFavoriteVersion } from './topicFavoriteVersion';

/**
 * Lock-screen / system now-playing artwork: resolve the topic cover through
 * the same chain as the mini-bar thumb (custom cover, else category cover),
 * and expose it as an absolute URL so iOS Media Session can fetch it.
 */
export function mediaSessionArtworkFor(media: Pick<MountedPlayer, 'topicId'>): MediaSessionArtworkImage[] | undefined {
	if (!media.topicId) return undefined;
	const topic = getTopic(media.topicId);
	const coverUrl = topic?.coverImageUrl ?? coverUrlForTopic(categoriesFromClassification(topic?.classification));
	if (!coverUrl) return undefined;
	const absolute = typeof location === 'undefined' ? coverUrl : new URL(coverUrl, location.href).href;
	return [{ src: absolute, sizes: '512x512', type: coverMimeType(absolute) }];
}

function coverMimeType(url: string): string {
	if (/\.jpe?g(?:$|\?)/i.test(url)) return 'image/jpeg';
	if (/\.png(?:$|\?)/i.test(url)) return 'image/png';
	return 'image/webp';
}

export interface NowPlayingChapter {
	index: number;
	title: string;
	topicTitle: string;
	topicSlug: string;
	topicSubtitle: string;
}

/** The compact player has one line for the playing chapter, so retain both
 * its ordinal and the server-backed chapter title in that line. */
export function formatMiniPlayerChapterTitle(index: number, title: string): string {
	const resolvedTitle = (title.trim() || `Chapter ${index}`).replace(/^ch(?:apter)?\.?\s*\d+(?:\s*[:\-–—]\s*|\s+)/i, '');
	return `Ch.${index}: ${resolvedTitle}`;
}

/** The now-playing identity is separate from the article being browsed. */
export function syncNowPlayingMedia(root: Root, media: MountedPlayer): void {
	if (media.kind !== 'audio' || !media.topicId || !media.chapterIndex) return;
	const page = (root as unknown as { __activeChapter?: Partial<NowPlayingChapter> }).__activeChapter;
	const samePageChapter = page?.topicSlug === media.topicId && page.index === media.chapterIndex;
	const topic = getTopic(media.topicId);
	const metadata = media.sessionMetadata;
	const indexedTitle = [...(topic?.previewChapters ?? []), ...(topic?.lockedChapters ?? [])]
		.find((chapter) => chapter.index === media.chapterIndex)?.title;
	const metadataTitle = metadata?.title?.trim();
	// Handoff metadata is authoritative once it has the real title. A generic
	// "Chapter N" fallback must never hide an indexed chapter title.
	const title = indexedTitle?.trim()
		|| (metadataTitle && metadataTitle !== `Chapter ${media.chapterIndex}` ? metadataTitle : undefined)
		|| (samePageChapter ? page?.title?.trim() : undefined)
		|| metadataTitle
		|| `Chapter ${media.chapterIndex}`;
	(root as unknown as { __nowPlayingChapter?: NowPlayingChapter }).__nowPlayingChapter = {
		index: media.chapterIndex,
		title,
		topicTitle: metadata?.artist || (samePageChapter ? page?.topicTitle : undefined) || topic?.title || 'DeepMox',
		topicSlug: media.topicId,
		topicSubtitle: samePageChapter ? page?.topicSubtitle || '' : topic?.subtitle || '',
	};
	// Mounting is allowed before the bar has ever been opened (for example a
	// lock-screen resume). Avoid starting page/playlist hydration in that case;
	// a visible player is repainted immediately and a later open reads this
	// committed identity.
	if (byId(root, 'webPlayer')?.classList.contains('visible')) showPlayer(root);
}

/** Clear a removed audio source only when it still owns the player projection. */
export function clearNowPlayingMedia(root: Root, media?: Pick<MountedPlayer, 'topicId' | 'chapterIndex'>): void {
	const holder = root as unknown as { __nowPlayingChapter?: NowPlayingChapter };
	const current = holder.__nowPlayingChapter;
	if (media && current && (current.topicSlug !== media.topicId || current.index !== media.chapterIndex)) return;
	delete holder.__nowPlayingChapter;
}

/**
 * Optimistic now-playing identity for an explicit play intent. The dock must
 * follow the tap immediately — an already-mounted previous chapter otherwise
 * keeps the bar's title/progress frozen until the token round-trip lands, which
 * reads as "not following". The authoritative mount overwrites this; a failed
 * start leaves the previewed chapter under the retry affordance, which is the
 * chapter a retry targets anyway.
 */
export function previewNowPlayingChapter(root: Root, chapter: NowPlayingChapter): void {
	(root as unknown as { __nowPlayingChapter?: NowPlayingChapter }).__nowPlayingChapter = chapter;
	showPlayer(root);
	// The Home hero is the same "continue here" story as the dock: an explicit
	// play intent flips it to the intended topic/chapter right away, matching
	// the authoritative state the in-flight progress write will produce.
	if (byId(root, 'paidHeroTitle')) {
		const titleEl = byId(root, 'paidHeroTitle');
		const subtitleEl = byId(root, 'paidHeroSubtitle');
		const action = byId(root, 'paidHeroPrimaryAction');
		if (titleEl) titleEl.textContent = chapter.topicTitle;
		if (subtitleEl) subtitleEl.textContent = `Ch.${chapter.index} · ${chapter.title}`;
		if (action) {
			action.textContent = `Continue Ch.${chapter.index}`;
			action.dataset.protoClick = 'resumeListening()';
		}
		const topicDescEl = byId(root, 'paidHeroTopicDesc');
		if (topicDescEl && !chapter.topicSubtitle) topicDescEl.textContent = '';
	}
}

export function showPlayer(root: Root) {
  byId(root, "webPlayer")?.classList.add("visible");
  root.querySelector(".app-layout")?.classList.add("has-player");
  const title = byId(root, "playerTitle");
  const topicName = byId(root, "playerTopicName");
  const thumb = byId(root, "playerThumb");
  // `__nowPlayingChapter` is committed only by a mounted audio source. Page
  // navigation may update `__activeChapter` while an older source is still
  // audible; it must never repaint the global player as a different topic.
  const holder = root as unknown as {
    __activeChapter?: NowPlayingChapter;
    __nowPlayingChapter?: NowPlayingChapter;
  };
  const active = holder.__nowPlayingChapter ?? holder.__activeChapter;
  // On a deep-linked chapter entry the stamp may still hold the article
  // skeleton ("Loading chapter…") if the Topic navigation read has not resolved by
  // the time the user hits Play. The article body is loaded by then, so
  // prefer its live titles over a placeholder stamp.
  const liveTitle = byId(root, "article-title")?.textContent;
  const liveTopic = byId(root, "article-topic-context")?.textContent;
	const resolvedChapterTitle =
		active?.title && active.title !== "Loading chapter…" && active.title !== "Pick a chapter to start"
			? active.title
			: liveTitle || active?.title || "Chapter";
	const resolvedTitle = active?.index ? formatMiniPlayerChapterTitle(active.index, resolvedChapterTitle) : resolvedChapterTitle;
	const resolvedTopic = active?.topicTitle ? active.topicTitle : liveTopic || "DeepMox";
	if (active && liveTitle && active === holder.__activeChapter && !holder.__nowPlayingChapter) active.title = resolvedChapterTitle;
  if (active && liveTopic && active === holder.__activeChapter && !holder.__nowPlayingChapter) active.topicTitle = resolvedTopic;
  const audioWaiting = byId(root, "webPlayer")?.dataset.mediaReadiness === 'audio';
  if (title) title.textContent = resolvedTitle;
  if (topicName && !audioWaiting) topicName.textContent = resolvedTopic;
  syncMiniPlayerFavorite(root, active?.topicSlug ?? getRuntime(root)?.activeCourseId);
  // Painting the topic cover on the mini bar thumb replaces the fixed
  // text-icon placeholder. Falls back to the category default when the
  // topic has not loaded yet (visibility preserved either way).
  if (thumb) {
    const topicSlug = active?.topicSlug;
    const topic = topicSlug ? getTopic(topicSlug) : undefined;
    const coverUrl = topic?.coverImageUrl ?? coverUrlForTopic(categoriesFromClassification(topic?.classification));
    void applyTopicCover(thumb, coverUrl);
  }
  // The expanded panel follows the topic-landing hierarchy: topic title as
  // the headline, topic subtitle secondary, chapter list below.
  paintNowPlayingPanel(root);
}

/** Hide the audio-only player while a video session owns presentation. */
export function hidePlayer(root: Root): void {
  byId(root, 'webPlayer')?.classList.remove('visible');
  root.querySelector('.app-layout')?.classList.remove('has-player');
}

/**
 * The mini-player favourite affordance is a second presentation of the
 * existing topic favourite action. It deliberately keeps its own state in
 * sync with `/api/user/favorites`, rather than introducing a player-specific
 * preference or write path.
 */
function syncMiniPlayerFavorite(root: Root, topicId: string | undefined): void {
  const button = byId(root, 'miniFavoriteBtn') as HTMLButtonElement | null;
  const normalizedTopicId = topicId?.trim() ?? '';
  if (!button) return;
  button.dataset.topicId = normalizedTopicId;
  button.disabled = !normalizedTopicId;
  if (!normalizedTopicId) {
    paintMiniPlayerFavorite(button, false);
		paintNowPlayingFavorite(root, false);
    button.setAttribute('aria-label', 'Current topic cannot be saved');
    return;
  }
	paintMiniPlayerFavorite(button, button.classList.contains('active'));
	const runtime = getRuntime(root);
	const favoriteVersionAtRead = topicFavoriteVersion(root, normalizedTopicId);
	void runtime?.api.listFavorites().then((response) => {
    // A chapter switch may have happened while the response was in flight.
    // Do not paint an older list response over the optimistic write state.
		if (button.dataset.topicId !== normalizedTopicId || button.dataset.favoritePending === 'true' || topicFavoriteVersion(root, normalizedTopicId) !== favoriteVersionAtRead) return;
		const favorited = (response.favorites ?? []).some((favorite) => favorite.topicSlug === normalizedTopicId);
		paintMiniPlayerFavorite(button, favorited);
		paintNowPlayingFavorite(root, favorited);
  }).catch(() => undefined);
}

export function paintMiniPlayerFavorite(button: HTMLElement, favorited: boolean): void {
  button.classList.toggle('active', favorited);
  button.setAttribute('aria-label', favorited ? 'Remove current topic from saved' : 'Save current topic');
}

function paintNowPlayingFavorite(root: Root, favorited: boolean): void {
	const button = byId(root, 'nowPlayingFavBtn');
	if (!button) return;
	button.classList.toggle('active', favorited);
	button.setAttribute('aria-pressed', String(favorited));
}

export function activateTier(source: HTMLElement) {
  const tier = source.closest<HTMLElement>(".sp-tier");
  if (!tier) return;
  tier.parentElement?.querySelectorAll<HTMLElement>(".sp-tier").forEach((node) => node.classList.remove("selected"));
  tier.classList.add("selected");
}
